import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { adminApi, AdminApiError, saveSession } from "../lib/adminApi";
import { AdminButton, Notice, TextInput } from "../components/admin/AdminPrimitives";
import AppHeader from "../components/AppHeader";
import type { AdminSession, LoginResponse } from "../types/admin";

/**
 * 관리자 로그인 화면.
 *
 * 서버가 내려준 메시지를 그대로 보여준다 — 아이디/비밀번호 오류는 뭉뚱그린 문구로
 * 오지만, "계정 미설정(503)" 이나 "시도 과다 잠금(429)" 은 원인을 알려줘야 조치할 수 있다.
 */
export default function AdminLoginPage({
  onSuccess,
}: {
  onSuccess: (session: AdminSession) => void;
}) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (pending) return;
    setError(null);
    setPending(true);
    try {
      const res = await adminApi.post<LoginResponse>("/api/admin/login", null, {
        username,
        password,
      });
      const session: AdminSession = {
        token: res.token,
        username: res.username,
        expiresAt: res.expiresAt,
      };
      saveSession(session);
      onSuccess(session);
    } catch (err) {
      setError(
        err instanceof AdminApiError
          ? err.message
          : "서버에 연결할 수 없습니다. 잠시 후 다시 시도해주세요.",
      );
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="min-h-full bg-slate-50 text-slate-900 dark:bg-surface-dark dark:text-slate-100">
      <AppHeader showAdminLink={false} />
      <main className="mx-auto flex max-w-md flex-col px-6 py-16">
        <h2 className="text-lg font-semibold">관리자 로그인</h2>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          임계값·알림 설정을 변경하려면 로그인이 필요합니다.
        </p>

        <form
          onSubmit={submit}
          className="mt-6 space-y-4 rounded-2xl border border-surface-border bg-surface p-5 shadow-sm dark:border-surface-darkBorder dark:bg-surface-darkSubtle"
        >
          <TextInput
            label="아이디"
            value={username}
            onChange={setUsername}
            autoComplete="username"
          />
          <TextInput
            label="비밀번호"
            type="password"
            value={password}
            onChange={setPassword}
            autoComplete="current-password"
          />

          {error && <Notice tone="error">{error}</Notice>}

          <AdminButton
            tone="primary"
            type="submit"
            disabled={pending || !username || !password}
          >
            {pending ? "로그인 중…" : "로그인"}
          </AdminButton>
        </form>

        <Link
          to="/"
          className="mt-6 text-xs text-slate-500 underline-offset-2 hover:underline dark:text-slate-400"
        >
          ← 상태 페이지로 돌아가기
        </Link>
      </main>
    </div>
  );
}
